/**
 * OptimizeSetupModal — Pre-flight style selector for AI Test Optimizer
 *
 * Intercepts the "Optimize with AI" action before any token is spent.
 * The user picks a writing style (BDD / TDD / Procedural) then confirms
 * with "Start Optimization", which triggers the actual API call upstream.
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { Sparkles, X } from 'lucide-react';
import type { WritingStyle } from './OptimizedTestCasesModal';

// ── Style definitions ──────────────────────────────────────────────────────────

interface IStyleOption {
    value: WritingStyle;
    label: string;
    tagline: string;
    description: string;
    example: string;
    accent: string;
    accentDark: string;
    ring: string;
}

const STYLE_OPTIONS: IStyleOption[] = [
    {
        value: 'bdd',
        label: 'BDD',
        tagline: 'Behaviour-Driven Development',
        description:
            'Steps are phrased as Given / When / Then scenarios, making them readable by product and QA stakeholders alike.',
        example: 'Given the user is logged in\nWhen they submit the checkout form\nThen the order confirmation page appears',
        accent: 'bg-violet-50 border-violet-300 text-violet-700',
        accentDark: 'dark:bg-violet-950/30 dark:border-violet-700 dark:text-violet-300',
        ring: 'ring-violet-500',
    },
    {
        value: 'tdd',
        label: 'TDD',
        tagline: 'Test-Driven Development',
        description:
            'Steps follow Setup / Execute / Assert structure, aligning with unit-test vocabulary and developer-centric workflows.',
        example: 'Setup: authenticated session with admin role\nExecute: POST /api/orders with valid payload\nAssert: response status is 201',
        accent: 'bg-sky-50 border-sky-300 text-sky-700',
        accentDark: 'dark:bg-sky-950/30 dark:border-sky-700 dark:text-sky-300',
        ring: 'ring-sky-500',
    },
    {
        value: 'procedural',
        label: 'Procedural',
        tagline: 'Step-by-Step',
        description:
            'Classic numbered steps in plain language. Best for manual testers, exploratory testing, and onboarding documentation.',
        example: '1. Open the application URL\n2. Enter valid credentials\n3. Verify the dashboard loads within 3 seconds',
        accent: 'bg-emerald-50 border-emerald-300 text-emerald-700',
        accentDark: 'dark:bg-emerald-950/30 dark:border-emerald-700 dark:text-emerald-300',
        ring: 'ring-emerald-500',
    },
];

// ── Props ──────────────────────────────────────────────────────────────────────

interface OptimizeSetupModalProps {
    isOpen: boolean;
    testCaseCount: number;
    onCancel: () => void;
    /** Called with the chosen style when the user confirms. */
    onConfirm: (style: WritingStyle) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function OptimizeSetupModal({
    isOpen,
    testCaseCount,
    onCancel,
    onConfirm,
}: OptimizeSetupModalProps) {
    const [selected, setSelected] = useState<WritingStyle>('bdd');

    // Reset to default style each time the modal opens so the user always
    // starts from a known state rather than seeing their last-session pick.
    useEffect(() => {
        if (isOpen) setSelected('bdd');
    }, [isOpen]);

    return (
        <Transition show={isOpen}>
            <Dialog onClose={onCancel} className="relative z-50">

                {/* Backdrop */}
                <TransitionChild
                    enter="transition-opacity duration-200 ease-out"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-150 ease-in"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-900/60 dark:bg-black/70" aria-hidden="true" />
                </TransitionChild>

                {/* Panel */}
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <TransitionChild
                        enter="transition-all duration-200 ease-out"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition-all duration-150 ease-in"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <DialogPanel className="w-full max-w-xl rounded-2xl bg-white dark:bg-gh-bg-dark border border-slate-200 dark:border-gh-border-dark shadow-2xl overflow-hidden">

                            {/* Header */}
                            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-200 dark:border-gh-border-dark">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400 shrink-0">
                                        <Sparkles size={18} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                            Select Optimization Style
                                        </h3>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                            Optimizing {testCaseCount} test case{testCaseCount !== 1 ? 's' : ''}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={onCancel}
                                    aria-label="Cancel"
                                    className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors shrink-0"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Style options */}
                            <div role="radiogroup" aria-label="Optimization style" className="px-6 py-5 flex flex-col gap-3">
                                {STYLE_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={selected === opt.value}
                                        onClick={() => setSelected(opt.value)}
                                        className={[
                                            'w-full text-left rounded-xl border-2 p-4 transition-all duration-150',
                                            selected === opt.value
                                                ? `border-violet-500 dark:border-violet-500 bg-violet-50 dark:bg-violet-950/20 ring-2 ${opt.ring} ring-offset-0`
                                                : 'border-slate-200 dark:border-gh-border-dark hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-gh-bg-dark',
                                        ].join(' ')}
                                    >
                                        <div className="flex items-start gap-3">
                                            {/* Radio indicator */}
                                            <div className={[
                                                'mt-0.5 flex-none w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors',
                                                selected === opt.value
                                                    ? 'border-violet-600 dark:border-violet-400 bg-violet-600 dark:bg-violet-500'
                                                    : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-transparent',
                                            ].join(' ')}>
                                                {selected === opt.value && (
                                                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                        {opt.label}
                                                    </span>
                                                    <span className="text-xs text-slate-500 dark:text-slate-400">
                                                        — {opt.tagline}
                                                    </span>
                                                </div>
                                                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                                    {opt.description}
                                                </p>
                                                {selected === opt.value && (
                                                    <pre className={[
                                                        'mt-2 px-3 py-2 rounded-lg text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words border',
                                                        opt.accent,
                                                        opt.accentDark,
                                                    ].join(' ')}>
                                                        {opt.example}
                                                    </pre>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-gh-border-dark bg-slate-50 dark:bg-gh-bg-subtle-dark">
                                <button
                                    type="button"
                                    onClick={onCancel}
                                    className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onConfirm(selected)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors shadow-sm"
                                >
                                    <Sparkles size={14} />
                                    Start Optimization
                                </button>
                            </div>

                        </DialogPanel>
                    </TransitionChild>
                </div>

            </Dialog>
        </Transition>
    );
}
